// client/src/coms/views/canvas_parts/canvas_room_view_parts/canvasUtils.js
const CANVAS_DIRTY_VERSION_KEY = '__voxemeraCanvasDirtyVersion';
const CANVAS_DIRTY_BOUNDS_KEY = '__voxemeraCanvasDirtyBounds';
const CANVAS_NATIVE_GET_CONTEXT_KEY = '__voxemeraNativeGetContext';
const CANVAS_DIRTY_TRACKING_INSTALLED_KEY = '__voxemeraDirtyTrackingInstalled';
const CONTEXT_DIRTY_PATCHED_KEY = '__voxemeraDirtyPatchedContext';

function toFiniteNumber(value, fallback = 0) {
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
}

function getCanvasWidth(canvasEl) {
  return Math.max(1, Math.floor(toFiniteNumber(canvasEl && canvasEl.width, 1)));
}

function getCanvasHeight(canvasEl) {
  return Math.max(1, Math.floor(toFiniteNumber(canvasEl && canvasEl.height, 1)));
}

function normaliseDirtyRect(canvasEl, x = 0, y = 0, width = null, height = null) {
  const canvasWidth = getCanvasWidth(canvasEl);
  const canvasHeight = getCanvasHeight(canvasEl);
  const rectX = Math.floor(toFiniteNumber(x, 0));
  const rectY = Math.floor(toFiniteNumber(y, 0));
  const rectWidth = width == null ? canvasWidth : Math.ceil(Math.abs(toFiniteNumber(width, canvasWidth)));
  const rectHeight = height == null ? canvasHeight : Math.ceil(Math.abs(toFiniteNumber(height, canvasHeight)));

  const left = Math.max(0, Math.min(canvasWidth, rectX));
  const top = Math.max(0, Math.min(canvasHeight, rectY));
  const right = Math.max(0, Math.min(canvasWidth, rectX + rectWidth));
  const bottom = Math.max(0, Math.min(canvasHeight, rectY + rectHeight));

  if (right <= left || bottom <= top) return null;
  return {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };
}

export function markCanvasDirty(canvasEl, x = 0, y = 0, width = null, height = null) {
  if (!canvasEl) return 0;

  const nextVersion = (Number(canvasEl[CANVAS_DIRTY_VERSION_KEY]) || 0) + 1;
  canvasEl[CANVAS_DIRTY_VERSION_KEY] = nextVersion;

  const nextBounds = normaliseDirtyRect(canvasEl, x, y, width, height);
  if (!nextBounds) return nextVersion;

  const previousBounds = canvasEl[CANVAS_DIRTY_BOUNDS_KEY];
  if (!previousBounds) {
    canvasEl[CANVAS_DIRTY_BOUNDS_KEY] = nextBounds;
    return nextVersion;
  }

  const left = Math.min(previousBounds.x, nextBounds.x);
  const top = Math.min(previousBounds.y, nextBounds.y);
  const right = Math.max(previousBounds.x + previousBounds.width, nextBounds.x + nextBounds.width);
  const bottom = Math.max(previousBounds.y + previousBounds.height, nextBounds.y + nextBounds.height);
  canvasEl[CANVAS_DIRTY_BOUNDS_KEY] = {
    x: left,
    y: top,
    width: right - left,
    height: bottom - top,
  };

  return nextVersion;
}

export function getCanvasDirtyVersion(canvasEl) {
  return Number(canvasEl && canvasEl[CANVAS_DIRTY_VERSION_KEY]) || 0;
}

export function getCanvasDirtyBounds(canvasEl) {
  const bounds = canvasEl && canvasEl[CANVAS_DIRTY_BOUNDS_KEY] ? canvasEl[CANVAS_DIRTY_BOUNDS_KEY] : null;
  if (!bounds) return null;
  return {
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
  };
}

export function clearCanvasDirtyBounds(canvasEl) {
  if (!canvasEl) return;
  canvasEl[CANVAS_DIRTY_BOUNDS_KEY] = null;
}

function getDrawImageDestinationRect(canvasEl, args) {
  if (!args || !args.length) return null;

  const source = args[0];
  const sourceWidth = Math.max(1, Math.floor(toFiniteNumber(source && (source.width || source.videoWidth || source.naturalWidth), getCanvasWidth(canvasEl))));
  const sourceHeight = Math.max(1, Math.floor(toFiniteNumber(source && (source.height || source.videoHeight || source.naturalHeight), getCanvasHeight(canvasEl))));

  if (args.length >= 9) {
    return {
      x: toFiniteNumber(args[5], 0),
      y: toFiniteNumber(args[6], 0),
      width: toFiniteNumber(args[7], sourceWidth),
      height: toFiniteNumber(args[8], sourceHeight),
    };
  }

  if (args.length >= 5) {
    return {
      x: toFiniteNumber(args[1], 0),
      y: toFiniteNumber(args[2], 0),
      width: toFiniteNumber(args[3], sourceWidth),
      height: toFiniteNumber(args[4], sourceHeight),
    };
  }

  if (args.length >= 3) {
    return {
      x: toFiniteNumber(args[1], 0),
      y: toFiniteNumber(args[2], 0),
      width: sourceWidth,
      height: sourceHeight,
    };
  }

  return null;
}

function patchCanvas2dContextForDirtyTracking(canvasEl, context) {
  if (!canvasEl || !context || context[CONTEXT_DIRTY_PATCHED_KEY]) return context;

  const patchMethod = (methodName, getBounds) => {
    const original = context[methodName];
    if (typeof original !== 'function') return;
    context[methodName] = function patchedCanvasDirtyMethod(...args) {
      const result = original.apply(this, args);
      const bounds = typeof getBounds === 'function' ? getBounds(args) : null;
      if (bounds) {
        markCanvasDirty(canvasEl, bounds.x, bounds.y, bounds.width, bounds.height);
      } else {
        markCanvasDirty(canvasEl);
      }
      return result;
    };
  };

  patchMethod('clearRect', (args) => ({
    x: toFiniteNumber(args[0], 0),
    y: toFiniteNumber(args[1], 0),
    width: toFiniteNumber(args[2], getCanvasWidth(canvasEl)),
    height: toFiniteNumber(args[3], getCanvasHeight(canvasEl)),
  }));
  patchMethod('fillRect', (args) => ({
    x: toFiniteNumber(args[0], 0),
    y: toFiniteNumber(args[1], 0),
    width: toFiniteNumber(args[2], getCanvasWidth(canvasEl)),
    height: toFiniteNumber(args[3], getCanvasHeight(canvasEl)),
  }));
  patchMethod('strokeRect', (args) => ({
    x: toFiniteNumber(args[0], 0) - Math.ceil(toFiniteNumber(context.lineWidth, 1)),
    y: toFiniteNumber(args[1], 0) - Math.ceil(toFiniteNumber(context.lineWidth, 1)),
    width: toFiniteNumber(args[2], getCanvasWidth(canvasEl)) + Math.ceil(toFiniteNumber(context.lineWidth, 1) * 2),
    height: toFiniteNumber(args[3], getCanvasHeight(canvasEl)) + Math.ceil(toFiniteNumber(context.lineWidth, 1) * 2),
  }));
  patchMethod('drawImage', (args) => getDrawImageDestinationRect(canvasEl, args));
  patchMethod('putImageData', (args) => ({
    x: toFiniteNumber(args[1], 0),
    y: toFiniteNumber(args[2], 0),
    width: Math.max(1, Math.floor(toFiniteNumber(args[0] && args[0].width, getCanvasWidth(canvasEl)))),
    height: Math.max(1, Math.floor(toFiniteNumber(args[0] && args[0].height, getCanvasHeight(canvasEl)))),
  }));
  patchMethod('fill');
  patchMethod('stroke');

  context[CONTEXT_DIRTY_PATCHED_KEY] = true;
  return context;
}

export function installCanvasDirtyTracking(canvasEl) {
  if (!canvasEl || typeof canvasEl.getContext !== 'function') return canvasEl;
  if (canvasEl[CANVAS_DIRTY_TRACKING_INSTALLED_KEY]) return canvasEl;

  canvasEl[CANVAS_DIRTY_VERSION_KEY] = Number(canvasEl[CANVAS_DIRTY_VERSION_KEY]) || 0;
  canvasEl[CANVAS_DIRTY_BOUNDS_KEY] = canvasEl[CANVAS_DIRTY_BOUNDS_KEY] || null;

  const nativeGetContext = canvasEl.getContext.bind(canvasEl);
  canvasEl[CANVAS_NATIVE_GET_CONTEXT_KEY] = nativeGetContext;
  canvasEl.getContext = function getTrackedCanvasContext(type, attributes) {
    const context = nativeGetContext(type, attributes);
    if (String(type || '').toLowerCase() === '2d') {
      return patchCanvas2dContextForDirtyTracking(canvasEl, context);
    }
    return context;
  };

  canvasEl[CANVAS_DIRTY_TRACKING_INSTALLED_KEY] = true;
  markCanvasDirty(canvasEl);
  return canvasEl;
}

export function createOffscreenCanvas(width, height) {
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, width);
  canvas.height = Math.max(1, height);
  return installCanvasDirtyTracking(canvas);
}

export function snapshotCanvas(canvasEl) {
  const ctx = canvasEl && canvasEl.getContext ? canvasEl.getContext('2d', { willReadFrequently: true }) : null;
  if (!ctx || !canvasEl) return null;
  try {
    const snapshot = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height);
    return snapshot;
  } catch {
    return null;
  }
}

export function restoreCanvas(canvasEl, imageData) {
  const ctx = canvasEl && canvasEl.getContext ? canvasEl.getContext('2d', { willReadFrequently: true }) : null;
  if (!ctx || !canvasEl) return;
  ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
  if (!imageData) {
    markCanvasDirty(canvasEl);
    return;
  }

  const sourceWidth = Math.max(1, Number(imageData.width) || 1);
  const sourceHeight = Math.max(1, Number(imageData.height) || 1);

  if (canvasEl.width === sourceWidth && canvasEl.height === sourceHeight) {
    ctx.putImageData(imageData, 0, 0);
    markCanvasDirty(canvasEl);
    return;
  }

  const tempCanvas = document.createElement('canvas');
  tempCanvas.width = sourceWidth;
  tempCanvas.height = sourceHeight;
  const tempCtx = tempCanvas.getContext('2d', { willReadFrequently: true });
  if (!tempCtx) return;
  tempCtx.putImageData(imageData, 0, 0);

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(tempCanvas, 0, 0);
  ctx.imageSmoothingEnabled = true;
  markCanvasDirty(canvasEl);
}

export function resizeCanvasPreservingContent(canvasEl, nextWidth, nextHeight) {
  if (!canvasEl) return;

  const width = Math.max(1, Math.floor(Number(nextWidth) || 1));
  const height = Math.max(1, Math.floor(Number(nextHeight) || 1));

  if (canvasEl.width === width && canvasEl.height === height) return;

  const snapshot = snapshotCanvas(canvasEl);
  canvasEl.width = width;
  canvasEl.height = height;
  installCanvasDirtyTracking(canvasEl);
  restoreCanvas(canvasEl, snapshot);
  markCanvasDirty(canvasEl);
}

export function clearCanvasElement(canvasEl) {
  const ctx = canvasEl && canvasEl.getContext ? canvasEl.getContext('2d', { willReadFrequently: true }) : null;
  if (!ctx || !canvasEl) return;
  ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
  markCanvasDirty(canvasEl);
}

export function canvasHasVisiblePixels(canvasEl) {
  if (!canvasEl || typeof canvasEl.getContext !== 'function') return false;

  const ctx = canvasEl.getContext('2d', { willReadFrequently: true });
  if (ctx) {
    try {
      const { data } = ctx.getImageData(0, 0, canvasEl.width, canvasEl.height);
      for (let i = 3; i < data.length; i += 4) {
        if (data[i] > 0) return true;
      }
    } catch {}
  }

  const gl = canvasEl.getContext('webgl');
  if (!gl) return false;

  try {
    const width = Math.max(1, canvasEl.width || 1);
    const height = Math.max(1, canvasEl.height || 1);
    const pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
    for (let i = 3; i < pixels.length; i += 4) {
      if (pixels[i] > 0) return true;
    }
  } catch {
    return false;
  }

  return false;
}
